//
// (c) 2016-2020 Mental Canvas Inc.
//
import {
    BufferAttribute,
    BufferGeometry,
    Camera,
    Mesh,
    Vector3
} from 'three';

export class Node {
    // Value: index into array of objects. (i.e., ID of associated Mesh / planar polygon)
    value: number = undefined
    back: Node = null
    front: Node = null
};

export class BSP {
    root: Node = new Node;
    bHasNormals: boolean = true;
    // For older scenes, will update the incoming geometries to merge triangles into planar polygons.
    constructor(parsedBSP: number[], TreeToCutlet: number[][], mcxVersion: number, geometries: Mesh[]) {
        this.bHasNormals = mcxVersion >= 2;
        var deserialize = function (array: number[], node: Node) {
            if (array.length == 0)
                return;
            var value = array[0];
            array.shift();
            if (value === -1)
                return;
            node.value = value;
            node.back = new Node;
            node.front = new Node;
            deserialize(array, node.front);
            deserialize(array, node.back);
        };
        // BSP extraction
        deserialize(parsedBSP, this.root);
        if (mcxVersion < 3)
            this.MergeGeometries(TreeToCutlet, geometries);
    }
    dispose() {
        // No circular references, don't really have to do anything.
        this.root = null;
    }
    private MergeGeometries(TreeToCutlet: number[][], geometries: Mesh[])
    {
       var MergeAdd = function (geoDest: BufferGeometry, geoSrc: BufferGeometry) {
            for (var key in geoDest.attributes) {
                var attribDest = <BufferAttribute>geoDest.attributes[key];
                var attribSrc = geoSrc.attributes[key];
                if (attribSrc === undefined) continue;
                // Concatenate two float32arrays.
                var newArray = new Float32Array(attribDest.array.length + attribSrc.array.length);
                newArray.set(attribDest.array);
                newArray.set(attribSrc.array, attribDest.array.length);
                attribDest.array = newArray;
                attribDest.count += attribSrc.count;
                attribDest.needsUpdate = true;
            }
            return geoDest;
        };
        for (var i = 0; i < TreeToCutlet.length; i++) {
            if (TreeToCutlet[i].length == 0) continue;
            // Merge subsequent geometries with the first one. Multipart polygons are
            // exported as a series of triangle faces built as a fan, and this recomposes
            // them into a single object.
            // TODO: just export the polygon in the first place!
            var dest = geometries[TreeToCutlet[i][0]];
            var j: number;
            for (j = 1; j < TreeToCutlet[i].length; j++) {
                var src = geometries[TreeToCutlet[i][j]];
                MergeAdd(<BufferGeometry>dest.geometry, <BufferGeometry>src.geometry);
            }
            for (j = 1; j < TreeToCutlet[i].length; j++) {
                geometries[TreeToCutlet[i][j]] = null;
            }
        }
        // Swap node.value - it starts as an index into TreeToCutlet; change it to an
        // index into geometries instead.
        var traverse = function (TreeToCutlet: number[][], node: Node): void {
            if (node.value === undefined) {
                return;
            }
            node.value = TreeToCutlet[node.value][0];
            traverse(TreeToCutlet, node.back);
            traverse(TreeToCutlet, node.front);
        };
        traverse(TreeToCutlet, this.root);
    }

    // Assign renderOrder to objects in geometries based on camera.
    SortGeometries(geometries: Mesh[], camera: Camera) {
        var counter = 0;
        // Returns true if the camera is in front of the given geometry's plane.
        var inFrontOf = function (geometry: Mesh, campos: Vector3, bHasNormals: boolean): boolean {
            var polynormal : Vector3;
            const positions = (<BufferGeometry>geometry.geometry).getAttribute('position').array;
            const pos0 = new Vector3(positions[0], positions[1], positions[2]);
            if (bHasNormals) {
                const normalAttrib = (<BufferGeometry>geometry.geometry).getAttribute('normal').array;
                polynormal = new Vector3(normalAttrib[0], normalAttrib[1], normalAttrib[2]);
                // Should already be normalized
            } else {
                // Use the first triangle of the geometry to get the plane.
                const pos1 = new Vector3(positions[3], positions[4], positions[5]);
                const pos2 = new Vector3(positions[6], positions[7], positions[8]);
                pos1.sub(pos0);
                pos2.sub(pos0);
                polynormal = pos1.clone();
                polynormal.cross(pos2);
                polynormal.normalize();
            }
            const plane_dist: number = -polynormal.dot(pos0);
            // Only differences from C++ code:
            // 1) plane_dist in C++ is a fixed canvas property, not derived from a dot product with triangle, though
            //    the dot product should be fairly stable.
            // 2) Epsilon below is 1e-37 in C++. Same difference, frankly.
            return campos.dot(polynormal) + plane_dist > 0;
        };
        var traverse = function (node: Node, bHasNormals: boolean): void {
            if (node.value === undefined) {
                return;
            }
            var theGeo = geometries[node.value];
            var bInFront = inFrontOf(theGeo, camera.position, bHasNormals);
            traverse(bInFront ? node.back : node.front, bHasNormals);
            theGeo.renderOrder = counter;
            counter++;
            traverse(bInFront ? node.front : node.back, bHasNormals);
        };

        traverse(this.root, this.bHasNormals);
    }

};

